import { z } from "zod/v4";

import {
  UpsellCompareAtPriceDisplayList,
  UpsellDiscountTypeList,
  UpsellProductRulesNode,
} from "../../../dtos/upsell-product-rules";
import { CartTargetingFeSchema } from "../../../fe-only/cart-targeting";

/** What one arm's block may differ in from another's. Count and window are shared by every arm. */
export const AuditPostPurchaseOwnedFieldList = [
  "blockPresent",
  "rules.action",
  "variables.discountType",
  "variables.discountValue",
  "countdownTimerEnabled",
  "timerCheckout",
] as const;

/** What a post-purchase test compares: offer against none, a subset of the three owned groups, or one offer turned on. */
export const AuditPostPurchaseRowList = [
  "offerVsNone",
  "products",
  "discount",
  "urgency",
  "products+discount",
  "products+urgency",
  "discount+urgency",
  "products+discount+urgency",
  "offerOnly",
] as const;

export const AuditPostPurchaseProductSourceList = [
  "bestSellers",
  "recommendations",
  "collection",
  "orderMapped",
  "reoffer",
  "named",
] as const;

/** One upsell rule: which orders it fires on, and the products it offers. No discount: the block's pair is the one charged. */
export const AuditPostPurchaseRuleSchema = z.object({
  priority: z.number().int().min(0),
  cartTargeting: CartTargetingFeSchema.omit({ id: true }),
  action: z.object({ expression: z.array(UpsellProductRulesNode) }),
});

/** `auditPostPurchaseProducts`: the products one or more arms offer. */
export const AuditPostPurchaseProductsValueSchema = z.object({
  armIds: z.array(z.string()).min(1),
  source: z.enum(AuditPostPurchaseProductSourceList),
  /** Stable across audits: what "not those products" names on a later turn. */
  identityKey: z.string(),
  display: z.string(),
  rules: z.array(AuditPostPurchaseRuleSchema).min(1),
  excludeItemsInCart: z.boolean(),
  excludeOutOfStock: z.boolean(),
});

/** `auditPostPurchaseTerms`: one arm's discount and countdown, with what an accepted offer earns. */
export const AuditPostPurchaseTermsValueSchema = z.object({
  armId: z.string(),
  tupleKey: z.string(),
  discountType: z.enum(UpsellDiscountTypeList),
  /** A fraction for a percentage, shop currency for a fixed amount, null for none. */
  discountValue: z.number().nullable(),
  /** The shop currency a fixed amount is in. Null for none and a percentage. */
  currency: z.string().nullable(),
  /** The discount as a share of the offered price: 0 for none. */
  dEff: z.number().min(0).max(1),
  countdown: z.boolean(),
  offerTimeoutMinutes: z.number().int().min(1).max(60),
  /** The countdown always runs the full window. */
  countdownDurationMinutes: z.number().int().min(1).max(60).nullable(),
  timerMode: z.literal("perVisitor").nullable(),
  compareAtPriceDisplay: z.enum(UpsellCompareAtPriceDisplayList),
  marginBasis: z.enum(["set", "store"]),
  margin: z.number().nullable(),
  /** More accepted offers this depth needs than no discount on the same products, to earn the same. */
  requiredAcceptanceLiftAgainstNone: z.number().nullable(),
  offeredPrice: z.number().nullable(),
  discountGivenAwayPerAccepted: z.number().nullable(),
  addOnProfitPerAccepted: z.number().nullable(),
});

/** `auditPostPurchaseLayout`: how many products every offer arm shows. */
export const AuditPostPurchaseLayoutValueSchema = z.object({
  maxProductLimit: z.number().int().min(1).max(10),
  descriptionEnabled: z.boolean(),
});

/** `auditPostPurchaseChange`: what the test compares, and which arm is control. */
export const AuditPostPurchaseChangeValueSchema = z.object({
  rowId: z.enum(AuditPostPurchaseRowList),
  differs: z.array(z.enum(AuditPostPurchaseOwnedFieldList)),
  arms: z
    .array(
      z.object({
        armId: z.string(),
        role: z.enum(["control", "treatment"]),
        /** Why this arm is control: no offer, the shallower discount, countdown off, then the products' name. */
        roleRule: z.enum([
          "empty",
          "pastControl",
          "shallowerDiscount",
          "countdownOff",
          "identity",
          "personalization",
        ]),
        /** False for the arm with no offer. */
        offer: z.boolean(),
      })
    )
    .min(1),
  /**
   * What the card says shows today and in each new version, written by code.
   * Null when today is not known well enough to say; absent on older cards.
   */
  plan: z
    .object({ today: z.string(), proposed: z.array(z.string()).min(1) })
    .nullable()
    .optional(),
  comparison: z.object({
    /** More accepted offers the treatment needs than control to earn the same. Null without two offer arms. */
    betweenArmsLift: z.number().nullable(),
    /** Shared shown products over the count. Unknown when an arm picks at order time. */
    productOverlapShare: z.union([z.number(), z.literal("unknown")]).nullable(),
    firstTest: z.boolean().nullable(),
  }),
});

/**
 * The key a terms tuple is known by, in every group and across audits: a
 * card's terms carry it so a later turn reads it rather than rebuilds it.
 */
export function tupleKeyOf(terms: {
  discountType: (typeof UpsellDiscountTypeList)[number];
  discountValue: number | null;
  countdown: boolean;
}): string {
  const value = terms.discountType === "none" ? "-" : terms.discountValue;
  return `${terms.discountType}:${value}:${terms.countdown ? "on" : "off"}`;
}

export type AuditPostPurchaseRule = z.infer<typeof AuditPostPurchaseRuleSchema>;
export type AuditPostPurchaseProductsValue = z.infer<
  typeof AuditPostPurchaseProductsValueSchema
>;
export type AuditPostPurchaseTermsValue = z.infer<
  typeof AuditPostPurchaseTermsValueSchema
>;
export type AuditPostPurchaseLayoutValue = z.infer<
  typeof AuditPostPurchaseLayoutValueSchema
>;
export type AuditPostPurchaseChangeValue = z.infer<
  typeof AuditPostPurchaseChangeValueSchema
>;
export type AuditPostPurchaseRow = (typeof AuditPostPurchaseRowList)[number];
export type AuditPostPurchaseOwnedField =
  (typeof AuditPostPurchaseOwnedFieldList)[number];
export type AuditPostPurchaseProductSource =
  (typeof AuditPostPurchaseProductSourceList)[number];
